import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Filter, Search, X } from 'lucide-react';
import { ActivityCategory } from '@/services/analytics/ActivityService';

interface DateRangeOption {
  label: string;
  value: string;
}

interface CategoryOption {
  label: string;
  value: ActivityCategory;
}

interface ActivityFilterBarProps {
  dateRange: string;
  dateRangeOptions: DateRangeOption[];
  onDateRangeChange: (value: string) => void;
  customStartDate: Date | null;
  customEndDate: Date | null;
  onCustomStartDateChange: (date: Date | null) => void;
  onCustomEndDateChange: (date: Date | null) => void;
  selectedCategories: ActivityCategory[];
  categoryOptions: CategoryOption[];
  onCategoryChange: (categories: ActivityCategory[]) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
}

const ActivityFilterBar: React.FC<ActivityFilterBarProps> = ({
  dateRange,
  dateRangeOptions,
  onDateRangeChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  selectedCategories,
  categoryOptions,
  onCategoryChange,
  searchTerm,
  onSearchChange,
}) => {
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const dateDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setIsDateDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDateDropdownOpen(false);
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleCategoryToggle = (category: ActivityCategory) => {
    if (selectedCategories.includes(category)) {
      onCategoryChange(selectedCategories.filter(c => c !== category));
    } else {
      onCategoryChange([...selectedCategories, category]);
    }
  };

  const clearFilters = () => {
    onDateRangeChange('last7days');
    onCustomStartDateChange(null);
    onCustomEndDateChange(null);
    onCategoryChange([]);
    onSearchChange('');
  };

  const hasActiveFilters = dateRange !== 'last7days' || selectedCategories.length > 0 || searchTerm !== '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="flex-grow md:flex-grow-0 min-w-[200px] md:min-w-[300px]">
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search activity..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button 
                className="absolute inset-y-0 right-3 flex items-center" 
                onClick={() => onSearchChange('')}
              >
                <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="relative" ref={dateDropdownRef}>
          <button
            className={`flex items-center gap-2 py-2.5 px-4 border ${isDateDropdownOpen ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'} rounded-lg bg-white hover:bg-gray-50`}
            onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
          >
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm">
              {dateRangeOptions.find(opt => opt.value === dateRange)?.label || 'Date Range'}
            </span>
          </button>

          {isDateDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 z-10 w-64 bg-white rounded-lg border border-gray-200 shadow-lg p-4">
              <div className="space-y-2">
                {dateRangeOptions.map(option => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dateRange"
                      value={option.value}
                      checked={dateRange === option.value}
                      onChange={() => onDateRangeChange(option.value)}
                      className="text-blue-500"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}

                {dateRange === 'custom' && (
                  <div className="pt-2 space-y-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={customStartDate ? customStartDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          onCustomStartDateChange(date);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">End Date</label>
                      <input
                        type="date"
                        value={customEndDate ? customEndDate.toISOString().split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value ? new Date(e.target.value) : null;
                          onCustomEndDateChange(date);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Category Filter */}
        <div className="relative" ref={categoryDropdownRef}>
          <button
            className={`flex items-center gap-2 py-2.5 px-4 border ${isCategoryDropdownOpen ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'} rounded-lg bg-white hover:bg-gray-50`}
            onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
          >
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm">
              {selectedCategories.length > 0
                ? `${selectedCategories.length} ${selectedCategories.length === 1 ? 'category' : 'categories'}`
                : 'All Categories'}
            </span>
          </button>

          {isCategoryDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 z-10 w-64 bg-white rounded-lg border border-gray-200 shadow-lg p-4">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {categoryOptions.map(option => (
                  <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(option.value)}
                      onChange={() => handleCategoryToggle(option.value)}
                      className="text-blue-500"
                    />
                    <span className="text-sm">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            className="flex items-center gap-1 py-2.5 px-4 text-sm text-gray-600 hover:text-gray-800"
            onClick={clearFilters}
          >
            <X className="h-4 w-4" />
            <span>Clear filters</span>
          </button>
        )}
      </div>

      {/* Active filters display */}
      {(selectedCategories.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {selectedCategories.map(category => (
            <div key={category} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs flex items-center gap-1">
              <span>{categoryOptions.find(opt => opt.value === category)?.label}</span>
              <button
                onClick={() => handleCategoryToggle(category)}
                className="h-4 w-4 rounded-full hover:bg-blue-100 flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFilterBar;
